// Client-side sensitive data scanner for CSV and text files
// This mirrors the backend DLP patterns for consistency

export type SensitiveDataType =
  | 'SSN'
  | 'DOB'
  | 'PHONE'
  | 'EMAIL'
  | 'CREDIT_CARD'
  | 'ADDRESS'
  | 'MEDICAL'
  | 'FINANCIAL'
  | 'POLICY_NUMBER'
  | 'DRIVERS_LICENSE';

// Regex patterns for detecting sensitive data
const SENSITIVE_DATA_PATTERNS: { type: SensitiveDataType; pattern: RegExp; minMatches?: number }[] = [
  // SSN: 123-45-6789 or 123456789
  { type: 'SSN', pattern: /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g, minMatches: 1 },
  // Credit Card: 16 digits
  { type: 'CREDIT_CARD', pattern: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, minMatches: 1 },
  // DOB patterns
  { type: 'DOB', pattern: /\b(?:0[1-9]|1[0-2])[-/](?:0[1-9]|[12]\d|3[01])[-/](?:19|20)\d{2}\b/g, minMatches: 3 },
  // Phone numbers
  { type: 'PHONE', pattern: /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, minMatches: 3 },
  // Email addresses
  { type: 'EMAIL', pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}/g, minMatches: 3 },
  // Policy numbers
  { type: 'POLICY_NUMBER', pattern: /\b[A-Z]{2,3}[-]?\d{6,10}\b/gi, minMatches: 3 },
];

// CSV column headers that indicate sensitive data
const SENSITIVE_COLUMN_HEADERS: { headers: string[]; type: SensitiveDataType }[] = [
  { headers: ['ssn', 'social_security', 'social security', 'social-security', 'ss#', 'ss_number'], type: 'SSN' },
  { headers: ['dob', 'date_of_birth', 'date of birth', 'birthdate', 'birth_date', 'birthday'], type: 'DOB' },
  { headers: ['phone', 'telephone', 'cell', 'mobile', 'phone_number', 'contact_number'], type: 'PHONE' },
  { headers: ['email', 'email_address', 'e-mail', 'mail'], type: 'EMAIL' },
  { headers: ['credit_card', 'cc_number', 'card_number', 'card_no', 'credit card'], type: 'CREDIT_CARD' },
  { headers: ['address', 'street', 'street_address', 'home_address', 'mailing_address'], type: 'ADDRESS' },
  { headers: ['dl', 'drivers_license', 'driver_license', 'license_number', 'dl_number'], type: 'DRIVERS_LICENSE' },
  { headers: ['diagnosis', 'medical_condition', 'prescription', 'medication', 'treatment'], type: 'MEDICAL' },
  { headers: ['account_number', 'routing_number', 'bank_account', 'tax_id', 'income', 'salary'], type: 'FINANCIAL' },
  { headers: ['policy_number', 'policy_no', 'policy_id', 'contract_number'], type: 'POLICY_NUMBER' },
];

export interface SensitiveDataScanResult {
  isSensitive: boolean;
  detectedTypes: SensitiveDataType[];
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Read file content as text
 */
async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Scan file content for sensitive data patterns
 */
export async function scanFileForSensitiveData(file: File): Promise<SensitiveDataScanResult> {
  // Only scan text-based files
  const textFileTypes = ['csv', 'txt', 'text'];
  const extension = file.name.split('.').pop()?.toLowerCase() || '';

  if (!textFileTypes.includes(extension) && !file.type.includes('text') && !file.type.includes('csv')) {
    return { isSensitive: false, detectedTypes: [], confidence: 'low' };
  }

  try {
    const content = await readFileAsText(file);
    return scanContentForSensitiveData(content, extension);
  } catch (error) {
    console.warn('Failed to scan file for sensitive data:', error);
    return { isSensitive: false, detectedTypes: [], confidence: 'low' };
  }
}

/**
 * Scan text content for sensitive data patterns
 */
export function scanContentForSensitiveData(content: string, fileType?: string): SensitiveDataScanResult {
  if (!content || typeof content !== 'string') {
    return { isSensitive: false, detectedTypes: [], confidence: 'low' };
  }

  const detectedTypes = new Set<SensitiveDataType>();
  const isCSV = fileType?.toLowerCase() === 'csv' || (content.includes(',') && content.includes('\n'));

  // Check CSV headers first (first line)
  if (isCSV) {
    const firstLine = content.split('\n')[0]?.toLowerCase() || '';
    for (const { headers, type } of SENSITIVE_COLUMN_HEADERS) {
      for (const header of headers) {
        if (firstLine.includes(header)) {
          detectedTypes.add(type);
          break;
        }
      }
    }
  }

  // Scan content for patterns
  for (const { type, pattern, minMatches = 1 } of SENSITIVE_DATA_PATTERNS) {
    const regex = new RegExp(pattern.source, pattern.flags);
    const matches = content.match(regex);
    const matchCount = matches?.length || 0;

    if (matchCount >= minMatches) {
      detectedTypes.add(type);
    }
  }

  // Determine confidence level
  let confidence: 'low' | 'medium' | 'high' = 'low';
  const typeCount = detectedTypes.size;

  if (typeCount >= 3) {
    confidence = 'high';
  } else if (typeCount >= 2) {
    confidence = 'medium';
  } else if (detectedTypes.has('SSN') || detectedTypes.has('CREDIT_CARD')) {
    confidence = 'medium';
  }

  return {
    isSensitive: detectedTypes.size > 0,
    detectedTypes: Array.from(detectedTypes),
    confidence,
  };
}
